import {
    Badge,
    Box,
    FileUploadProps,
    Input,
    Select,
    SelectProps,
    SpaceBetween,
    StatusIndicator,
    TableProps,
    WizardProps,
} from '@amzn/awsui-components-react'

// Wizard step indices. The navigation pane shows these as "Step 1" … "Step 4".
export const STEP_DOWNLOAD = 0
export const STEP_UPLOAD = 1
export const STEP_REVIEW = 2
export const STEP_SUBMISSION = 3

export enum CheckResult {
    PASS = 'pass',
    FAIL = 'fail',
}

export enum ValidationStatus {
    IDLE = 'idle',
    SUCCESS = 'success',
    ERROR = 'error',
}

export enum SubmissionStatus {
    IDLE = 'idle',
    IN_PROGRESS = 'in-progress',
    SUCCESS = 'success',
    ERROR = 'error',
}

export const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024

export interface ValidationChecks {
    size: CheckResult
    content: CheckResult
    headers: CheckResult
    data: CheckResult
    rows: CheckResult
}

export interface RowValidationError {
    // 1-based: the first data row after the header row is 1.
    rowNumber: number
    column: string
    value: string
    reason: string
}

export interface RowValidationResult {
    // 1-based: the first data row after the header row is 1.
    rowNumber: number
    result: CheckResult
    // One entry per failing field, used to render the details cell with a bold column name.
    failures: { column: string; reason: string }[]
    // Flattened "column: reason" lines (newline-separated), used for the error CSV export.
    details: string
}

export interface HrVarianceMetrics {
    total: string
    employee: string
    coop: string
}

export interface HrVariance {
    team_id: string
    team_name: string
    org_name: string
    group_name: string
    allocation_week: string
    row_numbers: number[] | null
    reported: HrVarianceMetrics
    hr: HrVarianceMetrics
    delta: HrVarianceMetrics
    mismatched_fields: string[]
}

export interface HrVarianceReasonRequest {
    team_id: string
    allocation_week: string
    hr_detail_note: string
}

export interface ImportErrorRow {
    lineNumbers: number[]
    sortKey: number
    location: string
    failures: { column: string | null; message: string }[]
}

export interface VarianceReasonState {
    reason: SelectProps.Option | null
    note: string
}

export const HR_VARIANCE_REASON_OPTIONS: SelectProps.Option[] = [
    { value: 'setting-up-future-weeks', label: 'Setting up future weeks' },
    { value: 'still-working-on-allocations', label: 'Still working on allocations' },
    { value: 'new-employees', label: 'New employees' },
    { value: 'other', label: 'Other' },
]

export const HR_VARIANCE_REASON_OTHER = 'other'

export const hrVarianceKey = (teamId: string, allocationWeek: string): string =>
    `${teamId}__${allocationWeek}`

export enum ColumnDataType {
    STRING = 'string',
    DECIMAL = 'decimal',
}

export interface TemplateColumn {
    id: string
    required: boolean
    dataType: ColumnDataType
    format?: string
    // Human-readable expected type shown in the data-type validation table.
    typeLabel: string
}

// Column ids that need special-case validation beyond their data type.
export const ALLOCATION_WEEK_COLUMN = 'Allocation Week'

// Employee and Co-op are individually optional, but every row must supply at least one of them.
export const EMPLOYEE_COLUMN = 'Employee'
export const COOP_COLUMN = 'Co-op'

export const DECIMAL_PATTERN = /^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/

// Friday, as returned by Date.prototype.getDay() (0 = Sunday).
export const FRIDAY_DAY_INDEX = 5

export type TemplateExampleRow = Record<string, string>

export const TEMPLATE_COLUMNS: TemplateColumn[] = [
    { id: 'Comment', required: false, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
    {
        id: ALLOCATION_WEEK_COLUMN,
        required: true,
        dataType: ColumnDataType.STRING,
        format: 'MM/DD/YYYY',
        typeLabel: 'Date (M/D/YYYY, M/D/YY, or YYYY-MM-DD, Friday)',
    },
    { id: 'Project (Service)', required: true, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
    { id: 'Program', required: true, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
    { id: EMPLOYEE_COLUMN, required: false, dataType: ColumnDataType.DECIMAL, typeLabel: 'Decimal' },
    { id: COOP_COLUMN, required: false, dataType: ColumnDataType.DECIMAL, typeLabel: 'Decimal' },
    { id: 'Organization', required: true, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
    { id: 'Group', required: true, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
    { id: 'Team', required: true, dataType: ColumnDataType.STRING, typeLabel: 'Text' },
]

export const EXPECTED_HEADERS = TEMPLATE_COLUMNS.map((column) => column.id)

export const TEMPLATE_EXAMPLE_ROWS: TemplateExampleRow[] = [
    {
        Comment: 'Example row — replace or delete before importing',
        'Allocation Week': '1/23/2026',
        'Project (Service)': 'KTLO',
        Program: 'KTLO AR',
        Employee: '9.8',
        'Co-op': '',
        Organization: 'WWTD',
        Group: 'TD SO',
        Team: 'TD PMO',
    },
    {
        Comment: 'Example row — replace or delete before importing',
        'Allocation Week': '1/23/2026',
        'Project (Service)': 'KTLO',
        Program: 'KTLO AFT',
        Employee: '1.2',
        'Co-op': '',
        Organization: 'WWTD',
        Group: 'TD SO',
        Team: 'WWTD',
    },
    {
        Comment: 'Example row — replace or delete before importing',
        'Allocation Week': '1/23/2026',
        'Project (Service)': 'KTLO',
        Program: 'KTLO AR',
        Employee: '4',
        'Co-op': '',
        Organization: 'WWTD',
        Group: 'TD SO',
        Team: 'TD FS',
    },
]

export const WIZARD_I18N_STRINGS: WizardProps.I18nStrings = {
    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
    collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
    navigationAriaLabel: 'Steps',
    cancelButton: 'Cancel',
    previousButton: 'Previous',
    nextButton: 'Next',
    submitButton: 'Submit',
}

export const FILE_UPLOAD_I18N_STRINGS: FileUploadProps.I18nStrings = {
    uploadButtonText: () => 'Choose file',
    dropzoneText: () => 'Drop file to upload',
    removeFileAriaLabel: (fileIndex: number) => `Remove file ${fileIndex + 1}`,
    limitShowFewer: 'Show fewer files',
    limitShowMore: 'Show more files',
    errorIconAriaLabel: 'Error',
    warningIconAriaLabel: 'Warning',
}

export const TEMPLATE_PREVIEW_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<TemplateExampleRow>[] =
    TEMPLATE_COLUMNS.map((column) => ({
        id: column.id,
        header: (
            <SpaceBetween size='xxs'>
                <SpaceBetween direction='horizontal' size='xs' alignItems='center'>
                    <span>{column.id}</span>
                    <Badge color={column.required ? 'blue' : 'grey'}>
                        {column.required ? 'Required' : 'Optional'}
                    </Badge>
                </SpaceBetween>
                {column.format && (
                    <Box variant='small' color='text-body-secondary' fontWeight='normal'>
                        Format: {column.format}
                    </Box>
                )}
            </SpaceBetween>
        ),
        cell: (item: TemplateExampleRow) => item[column.id],
    }))

export const ROW_VALIDATION_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<RowValidationResult>[] =
    [
        {
            id: 'location',
            header: 'Location',
            cell: (item: RowValidationResult) => `Line ${item.rowNumber + 1}`,
            sortingField: 'rowNumber',
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item: RowValidationResult) =>
                item.result === CheckResult.PASS ? (
                    <StatusIndicator type='success'>Passed</StatusIndicator>
                ) : (
                    <StatusIndicator type='error'>Failed</StatusIndicator>
                ),
            sortingField: 'result',
        },
        {
            id: 'details',
            header: 'Details',
            // Each failing field on its own line, with the column name in bold.
            cell: (item: RowValidationResult) =>
                item.failures.length > 0 ? (
                    <SpaceBetween size='xxs'>
                        {item.failures.map((failure, index) => (
                            <span key={index}>
                                <b>{failure.column}:</b> {failure.reason}
                            </span>
                        ))}
                    </SpaceBetween>
                ) : (
                    '-'
                ),
        },
    ]

export const IMPORT_ERROR_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<ImportErrorRow>[] = [
    {
        id: 'location',
        header: 'Location',
        cell: (item: ImportErrorRow) => item.location,
        sortingField: 'sortKey',
    },
    {
        id: 'details',
        header: 'Details',
        cell: (item: ImportErrorRow) => (
            <SpaceBetween size='xxs'>
                {item.failures.map((failure, index) => (
                    <span key={index}>
                        {failure.column ? <b>{failure.column}: </b> : null}
                        {failure.message}
                    </span>
                ))}
            </SpaceBetween>
        ),
    },
]

const renderHrMetric = (
    variance: HrVariance,
    field: 'total' | 'employee' | 'coop',
    label: string,
) => {
    const mismatched = variance.mismatched_fields.includes(field)
    return (
        <div>
            <Box variant='awsui-key-label'>{label}</Box>
            <Box
                fontWeight={mismatched ? 'bold' : 'normal'}
                color={mismatched ? 'text-status-warning' : undefined}
            >
                {variance.reported[field]} reported vs {variance.hr[field]} expected
                {mismatched ? ` (Δ ${variance.delta[field]})` : ''}
            </Box>
        </div>
    )
}

export const formatLineLocation = (lineNumbers: number[] | null): string => {
    const lines = lineNumbers ?? []
    return lines.length === 0
        ? 'File / team-level'
        : lines.length === 1
          ? `Line ${lines[0]}`
          : `Lines ${lines.join(', ')}`
}

export const buildHrVarianceColumnDefinitions = (
    reasonStateByKey: Record<string, VarianceReasonState>,
    onReasonChange: (key: string, reason: SelectProps.Option | null) => void,
    onNoteChange: (key: string, note: string) => void,
): TableProps.ColumnDefinition<HrVariance>[] => [
    {
        id: 'location',
        header: 'Location',
        cell: (item: HrVariance) => formatLineLocation(item.row_numbers),
        sortingComparator: (a: HrVariance, b: HrVariance) =>
            (a.row_numbers?.[0] ?? 0) - (b.row_numbers?.[0] ?? 0),
        width: 120,
    },
    {
        id: 'team',
        header: 'Team (Org › Group)',
        cell: (item: HrVariance) => (
            <SpaceBetween size='xxs'>
                <Box fontWeight='bold'>{item.team_name}</Box>
                <Box variant='small' color='text-body-secondary'>
                    {item.org_name} › {item.group_name}
                </Box>
            </SpaceBetween>
        ),
        sortingField: 'team_name',
    },
    {
        id: 'week',
        header: 'Allocation week',
        cell: (item: HrVariance) => item.allocation_week,
        sortingField: 'allocation_week',
    },
    {
        id: 'variance',
        header: 'Reported vs expected',
        cell: (item: HrVariance) => (
            <SpaceBetween size='xxs'>
                {renderHrMetric(item, 'total', 'Total')}
                {renderHrMetric(item, 'employee', 'Employee')}
                {renderHrMetric(item, 'coop', 'Co-op')}
            </SpaceBetween>
        ),
    },
    {
        id: 'reason',
        header: 'Reason for variance',
        cell: (item: HrVariance) => {
            const key = hrVarianceKey(item.team_id, item.allocation_week)
            const state = reasonStateByKey[key] ?? { reason: null, note: '' }
            const needsNote = state.reason?.value === HR_VARIANCE_REASON_OTHER
            return (
                <SpaceBetween size='xs'>
                    <Select
                        selectedOption={state.reason}
                        options={HR_VARIANCE_REASON_OPTIONS}
                        placeholder='Select a reason'
                        onChange={({ detail }) =>
                            onReasonChange(key, detail.selectedOption ?? null)
                        }
                        ariaLabel={`Reason for ${item.team_name} on ${item.allocation_week}`}
                        expandToViewport
                    />
                    {needsNote && (
                        <Input
                            value={state.note}
                            placeholder='Add detail'
                            onChange={({ detail }) => onNoteChange(key, detail.value)}
                            ariaLabel={`Reason detail for ${item.team_name} on ${item.allocation_week}`}
                        />
                    )}
                </SpaceBetween>
            )
        },
    },
]
